import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';
import { fullArticles } from '@/mocks/article';

interface NavbarProps {
  onCategoryClick?: () => void;
  onSafetyClick?: () => void;
  onFaqClick?: () => void;
  onArticleClick?: () => void;
}

export default function Navbar({ onCategoryClick, onSafetyClick, onFaqClick, onArticleClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const [mobilePoradnikiOpen, setMobilePoradnikiOpen] = useState(false);

  const articleSlugs = Object.keys(fullArticles);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (handler?: () => void) => {
    setMobileOpen(false);
    if (handler) handler();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center transition-transform group-hover:scale-105">
              <i className="ri-file-list-3-line text-lg text-background-50"></i>
            </div>
            <span className="text-lg font-heading font-bold text-foreground-900 whitespace-nowrap">
              Kakobuy <span className="text-primary-600">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-home-4-line mr-1.5"></i>
              Strona główna
            </Link>
            <button
              onClick={() => handleNavClick(onCategoryClick)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Kategorie
            </button>
            <div className="relative group">
              <Link
                to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
                className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap inline-flex items-center gap-1"
              >
                Poradniki
                <i className="ri-arrow-down-s-line text-xs"></i>
              </Link>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-40">
                <div className="bg-background-50 border border-background-200/70 rounded-lg shadow-sm py-2 min-w-[220px]">
                  {articleSlugs.map((slug) => {
                    const article = fullArticles[slug];
                    return (
                      <Link
                        key={slug}
                        to={`/artykul/${slug}`}
                        className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      >
                        {article.h1.slice(0, 45)}...
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
            <button
              onClick={() => handleNavClick(onSafetyClick)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              Bezpieczeństwo
            </button>
            <button
              onClick={() => handleNavClick(onFaqClick)}
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              FAQ
            </button>
            <Link
              to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-bag-3-line mr-1.5"></i>
              Przewodnik zakupowy
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 space-y-0.5 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-home-4-line text-base text-primary-500"></i>
            Strona główna
          </Link>

          <button
            onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <span className="flex items-center gap-2.5">
              <i className="ri-grid-line text-base text-primary-500"></i>
              Kategorie
            </span>
            <i className={`text-sm transition-transform ${mobileCategoriesOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
          </button>

          {mobileCategoriesOpen && (
            <div className="pl-4 pr-2 py-1 space-y-0.5 border-l-2 border-primary-100 ml-4">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-sm text-foreground-400`}></i>
                  {cat.name}
                </a>
              ))}
            </div>
          )}

          <button
            onClick={() => setMobilePoradnikiOpen(!mobilePoradnikiOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <span className="flex items-center gap-2.5">
              <i className="ri-book-open-line text-base text-primary-500"></i>
              Poradniki
            </span>
            <i className={`text-sm transition-transform ${mobilePoradnikiOpen ? 'rotate-180' : ''} ri-arrow-down-s-line`}></i>
          </button>

          {mobilePoradnikiOpen && (
            <div className="pl-4 pr-2 py-1 space-y-0.5 border-l-2 border-primary-100 ml-4">
              {articleSlugs.map((slug) => {
                const article = fullArticles[slug];
                return (
                  <Link
                    key={slug}
                    to={`/artykul/${slug}`}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 text-sm text-foreground-600 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-article-line text-sm text-foreground-400"></i>
                    {article.h1.slice(0, 40)}...
                  </Link>
                );
              })}
            </div>
          )}

          <Link
            to="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-shopping-bag-3-line text-base text-primary-500"></i>
            Przewodnik zakupowy
          </Link>

          <button
            onClick={() => handleNavClick(onSafetyClick)}
            className="w-full text-left flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-shield-check-line text-base text-primary-500"></i>
            Bezpieczeństwo
          </button>

          <button
            onClick={() => handleNavClick(onFaqClick)}
            className="w-full text-left flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
          >
            <i className="ri-question-line text-base text-primary-500"></i>
            FAQ
          </button>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2.5 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
            onClick={() => setMobileOpen(false)}
          >
            <i className="ri-file-list-3-line text-base text-primary-500"></i>
            Spreadsheet
          </a>

          <div className="pt-3 px-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="block w-full text-center px-5 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-all whitespace-nowrap"
            >
              Kakobuy Spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}